import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Input, 
  Select, 
  Checkbox, 
  Button, 
  Space, 
  Collapse,
  Typography,
  Badge
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined, 
  ClearOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import { FilterOption } from '../../types';

const { Option } = Select;
const { Panel } = Collapse;
const { Text } = Typography;

interface FilterGroup {
  key: string;
  label: string;
  type: 'select' | 'multiSelect' | 'checkbox' | 'input';
  options?: FilterOption[];
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  allowClear?: boolean;
  showCount?: boolean;
}

interface FilterPanelProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterGroups: FilterGroup[];
  onClearAll: () => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  totalFiltersApplied?: number;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  searchValue,
  onSearchChange,
  filterGroups,
  onClearAll,
  expanded = false,
  onExpandedChange,
  totalFiltersApplied = 0,
}) => {
  const renderFilterControl = (group: FilterGroup) => {
    switch (group.type) {
      case 'input':
        return (
          <Input
            value={group.value}
            onChange={(e) => group.onChange(e.target.value)}
            placeholder={group.placeholder}
            allowClear={group.allowClear}
          />
        );
      
      case 'select':
        return (
          <Select
            value={group.value}
            onChange={group.onChange}
            placeholder={group.placeholder}
            allowClear={group.allowClear}
            style={{ width: '100%' }}
          >
            {group.options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
                {group.showCount && option.count !== undefined && (
                  <Text type="secondary"> ({option.count})</Text>
                )}
              </Option>
            ))}
          </Select>
        );
      
      case 'multiSelect':
        return (
          <Select
            mode="multiple"
            value={group.value}
            onChange={group.onChange}
            placeholder={group.placeholder}
            allowClear={group.allowClear}
            style={{ width: '100%' }}
            maxTagCount="responsive"
          >
            {group.options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
                {group.showCount && option.count !== undefined && (
                  <Text type="secondary"> ({option.count})</Text>
                )}
              </Option>
            ))}
          </Select>
        );
      
      case 'checkbox':
        return (
          <Checkbox
            checked={group.value}
            onChange={(e) => group.onChange(e.target.checked)}
          >
            {group.label}
          </Checkbox>
        );
      
      default:
        return null;
    }
  };

  return (
    <Card 
      size="small" 
      title={
        <Space>
          <FilterOutlined />
          Filters
          {totalFiltersApplied > 0 && (
            <Badge count={totalFiltersApplied} showZero={false} />
          )}
        </Space>
      }
      extra={
        <Space>
          <Button 
            type="link" 
            size="small" 
            onClick={onClearAll}
            disabled={totalFiltersApplied === 0}
          >
            <ClearOutlined /> Clear All
          </Button>
          {onExpandedChange && (
            <Button
              type="text"
              size="small"
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => onExpandedChange(!expanded)}
            />
          )}
        </Space>
      }
    >
      {/* Search bar - always visible */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            allowClear
            size="large"
          />
        </Col>
      </Row>

      {/* Collapsible filters */}
      {expanded && (
        <Row gutter={[16, 16]}>
          {filterGroups.map((group) => (
            <Col xs={24} sm={12} md={8} lg={6} key={group.key}>
              <div>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                  {group.label}
                  {group.type === 'multiSelect' && Array.isArray(group.value) && group.value.length > 0 && (
                    <Badge count={group.value.length} style={{ marginLeft: 8 }} />
                  )}
                </Text>
                {renderFilterControl(group)}
              </div>
            </Col>
          ))}
        </Row>
      )}
    </Card>
  );
};

export default FilterPanel; 